import React, { useState, useEffect } from "react";
import { contactsAPI } from "../../../services/api";
import { toast } from "react-toastify";
import { Download, Upload, CheckCircle, X, FileUp, FileSpreadsheet } from "lucide-react";

// ── System fields hidden from import template selector ───────────────────────
const HIDDEN_FIELDS = new Set([
  "id",
  "organizationId",
  "createdBy",
  "assignedTo",
  "isShared",
  "createdAt",
  "updatedAt",
  "lastActivity",
  "companyId",    // managed via company linkage, not raw import
  "companyName",  // auto-resolved from companyId
]);

const BRAND      = "#1C4E6D";
const BRAND_DARK = "#153a4f";
const BRAND_LIGHT = "#e8f4f9";

const ImportContactsModal = ({ onClose, onImport }) => {
  // null = mode chooser, "template" = 3-step, "direct" = direct upload, "done" = result
  const [mode, setMode]               = useState(null);
  const [step, setStep]               = useState(1);        // template mode: 1→2→3
  const [columns, setColumns]         = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [file, setFile]               = useState(null);
  const [importResult, setImportResult] = useState(null);   // { successCount, failedCount, totalRecords, errors }

  useEffect(() => { fetchColumns(); }, []);

  const fetchColumns = async () => {
    setLoading(true);
    try {
      const response = await contactsAPI.getColumns();
      const formatted = (response.data || [])
        .filter(col => !HIDDEN_FIELDS.has(col))
        .map(col => ({
          value: col,
          label: col.replace(/([A-Z])/g, " $1").trim().replace(/^./, s => s.toUpperCase()),
        }));
      setColumns(formatted);
    } catch {
      toast.error("Failed to fetch columns");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (field) =>
    setSelectedFields(prev =>
      prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]
    );

  /* ── Generate & download template ── */
  const handleGenerateTemplate = async () => {
    if (selectedFields.length === 0) { toast.warning("Please select at least one field"); return; }
    setLoading(true);
    try {
      const response = await contactsAPI.generateTemplate(selectedFields);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "contact_import_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded successfully!");
      setStep(2);
    } catch {
      toast.error("Failed to generate template");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const valid = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    if (!valid.includes(f.type)) { toast.error("Please upload a valid Excel file (.xlsx or .xls)"); return; }
    setFile(f);
  };

  /* ── Shared import handler ── */
  const handleImport = async () => {
    if (!file) { toast.warning("Please select a file to import"); return; }
    setLoading(true);
    try {
      const response = await contactsAPI.importContacts(file);
      const data = response.data;
      setImportResult(data);

      if (data.successCount > 0)
        toast.success(`Successfully imported ${data.successCount} of ${data.totalRecords} contacts!`);
      if (data.failedCount > 0)
        toast.warning(`${data.failedCount} record(s) failed — see details below.`);

      if (mode === "template") setStep(3);
      else setMode("done");

      onImport?.();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to import contacts");
    } finally {
      setLoading(false);
    }
  };

  const downloadErrorCSV = () => {
    const errors = importResult?.errors || [];
    const header = "Row,Email / Name,Reason";
    const rows = errors.map(e => {
      const identifier = e.email || [e.firstName, e.lastName].filter(Boolean).join(" ") || "";
      return [String(e.row ?? ""), identifier, e.reason || ""]
        .map(v => `"${String(v).replace(/"/g, '""')}"`)
        .join(",");
    });
    const csv = [header, ...rows].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "contact_import_errors.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  /* =============================
     SHARED: Upload drop-zone
     ============================= */
  const UploadZone = () => (
    <div className="space-y-4">
      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
        <h3 className="text-base font-semibold text-gray-900 mb-1">
          {mode === "template" ? "Step 2: Upload Filled Template" : "Upload Excel File"}
        </h3>
        <p className="text-sm text-gray-600">
          {mode === "template"
            ? "Fill the downloaded template with your contact data and upload it here. Keep the column headers unchanged."
            : "Upload your Excel file with contact data. The first row must contain column headers matching the contact fields."}
        </p>
      </div>

      <label className="block border-2 border-dashed border-gray-300 rounded-lg p-10 text-center hover:border-[#1C4E6D] transition cursor-pointer"
        style={{ "--hover-border": BRAND }}>
        <Upload size={52} className="text-gray-400 mx-auto mb-3" />
        <span className="text-base font-semibold" style={{ color: BRAND }}>
          Click to choose file
        </span>
        <p className="text-sm text-gray-500 mt-1">or drag and drop your Excel file here</p>
        <p className="text-xs text-gray-400 mt-1">Supported formats: .xlsx, .xls</p>
        <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} className="hidden" />
      </label>

      {file && (
        <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-green-900 truncate">{file.name}</p>
            <p className="text-xs text-green-700">{(file.size / 1024).toFixed(2)} KB · Ready to import</p>
          </div>
          <button onClick={() => setFile(null)} className="text-gray-400 hover:text-red-500 transition">
            <X size={16} />
          </button>
        </div>
      )}
    </div>
  );

  /* =============================
     Done / result panel
     ============================= */
  const DonePanel = () => (
    <div className="py-8 space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <CheckCircle size={36} className="text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-1">Import Complete</h3>
        {importResult && (
          <p className="text-sm text-gray-500">
            {importResult.successCount} imported · {importResult.failedCount} failed · {importResult.totalRecords} total
          </p>
        )}
      </div>

      {importResult && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Total",    value: importResult.totalRecords, cls: "bg-blue-50 text-blue-700"   },
            { label: "Imported", value: importResult.successCount, cls: "bg-green-50 text-green-700" },
            { label: "Failed",   value: importResult.failedCount,  cls: "bg-red-50 text-red-700"    },
          ].map(({ label, value, cls }) => (
            <div key={label} className={`rounded-lg p-3 text-center ${cls}`}>
              <p className="text-2xl font-bold">{value}</p>
              <p className="text-xs font-medium mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      )}

      {importResult?.errors?.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-red-600">
              Failed rows ({importResult.errors.length})
            </p>
            <button
              onClick={downloadErrorCSV}
              className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded px-2.5 py-1.5 hover:bg-gray-50 transition"
            >
              <Download size={13} />
              Download error report
            </button>
          </div>
          <div className="max-h-52 overflow-y-auto border border-red-200 rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-red-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-red-700 w-12">Row</th>
                  <th className="px-3 py-2 text-left font-semibold text-red-700">Email / Name</th>
                  <th className="px-3 py-2 text-left font-semibold text-red-700">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-red-100">
                {importResult.errors.map((err, i) => (
                  <tr key={i} className="bg-white hover:bg-red-50">
                    <td className="px-3 py-2 font-semibold text-red-700">{err.row}</td>
                    <td className="px-3 py-2 text-gray-700">
                      {err.email || [err.firstName, err.lastName].filter(Boolean).join(" ") || "—"}
                    </td>
                    <td className="px-3 py-2 text-red-600">{err.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  /* =============================
     Steps bar (template mode)
     ============================= */
  const StepsBar = () => (
    <div className="flex items-center justify-center gap-2 px-6 py-4 bg-gray-50 border-b flex-shrink-0">
      {[
        { num: 1, label: "Select Fields" },
        { num: 2, label: "Upload File"   },
        { num: 3, label: "Complete"      },
      ].map((item, i) => (
        <React.Fragment key={item.num}>
          <div className="flex flex-col items-center">
            <div className={`flex items-center justify-center w-9 h-9 rounded-full text-sm font-semibold transition-colors ${
              step >= item.num ? "text-white" : "bg-gray-200 text-gray-600"
            }`} style={step >= item.num ? { backgroundColor: BRAND } : {}}>
              {step > item.num ? <CheckCircle size={18} /> : item.num}
            </div>
            <span className="text-xs text-gray-600 mt-1">{item.label}</span>
          </div>
          {i < 2 && (
            <div className={`w-16 h-1 transition-colors ${step > item.num ? "" : "bg-gray-200"}`}
              style={step > item.num ? { backgroundColor: BRAND } : {}} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  /* =============================
     RENDER
     ============================= */
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} />

      <div className="relative bg-white shadow-2xl w-full max-w-3xl h-full flex flex-col overflow-hidden"
        style={{ animation: "slideInRight 0.3s ease-out" }}>

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b text-white flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${BRAND}, #2a6a9f)` }}>
          <div>
            <h2 className="text-xl font-semibold">Import Contacts</h2>
            <p className="text-sm text-blue-100 mt-0.5">Upload contacts in bulk using Excel</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition">
            <X size={22} />
          </button>
        </div>

        {/* ── Mode chooser ── */}
        {mode === null && (
          <>
            <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8 py-10">
              <p className="text-base font-semibold text-gray-700">How would you like to import?</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 w-full max-w-xl">

                <button onClick={() => { setMode("template"); setStep(1); }}
                  className="flex flex-col items-center gap-3 p-6 border-2 border-gray-200 rounded-xl transition group text-left"
                  onMouseEnter={e => { e.currentTarget.style.borderColor = BRAND; e.currentTarget.style.backgroundColor = BRAND_LIGHT; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.backgroundColor = ''; }}>
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center transition"
                    style={{ color: BRAND }}>
                    <FileSpreadsheet size={22} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">Generate Template</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Choose which fields to include, download an Excel template, fill it in, then upload.
                    </p>
                  </div>
                </button>

                <button onClick={() => setMode("direct")}
                  className="flex flex-col items-center gap-3 p-6 border-2 border-gray-200 rounded-xl transition group text-left"
                  onMouseEnter={e => { e.currentTarget.style.borderColor = BRAND; e.currentTarget.style.backgroundColor = BRAND_LIGHT; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.backgroundColor = ''; }}>
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center"
                    style={{ color: "#16a34a" }}>
                    <FileUp size={22} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">Direct Upload</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Already have an Excel file ready? Upload it directly without generating a template first.
                    </p>
                  </div>
                </button>
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end flex-shrink-0">
              <button onClick={onClose}
                className="px-5 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
                Cancel
              </button>
            </div>
          </>
        )}

        {/* ── Template mode ── */}
        {mode === "template" && (
          <>
            <StepsBar />
            <div className="flex-1 overflow-y-auto px-6 py-6">

              {/* Step 1 — Select fields */}
              {step === 1 && (
                <div>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6">
                    <h3 className="text-base font-semibold text-gray-900 mb-1">Select Fields for Template</h3>
                    <p className="text-sm text-gray-600">
                      Choose the fields to include as column headers in the downloaded template.
                    </p>
                  </div>

                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-600" />
                      <span className="ml-3 text-gray-500 text-sm">Loading fields…</span>
                    </div>
                  ) : (
                    <>
                      <div className="mb-4 flex items-center justify-between">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold">{selectedFields.length}</span> fields selected
                        </p>
                        <div className="flex gap-2 text-xs">
                          <button onClick={() => setSelectedFields(columns.map(c => c.value))}
                            className="font-medium" style={{ color: BRAND }}>Select All</button>
                          <span className="text-gray-400">|</span>
                          <button onClick={() => setSelectedFields([])}
                            className="text-gray-600 hover:text-gray-700 font-medium">Clear All</button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {columns.map(col => (
                          <label key={col.value}
                            className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 cursor-pointer transition">
                            <input type="checkbox" checked={selectedFields.includes(col.value)}
                              onChange={() => handleCheckboxChange(col.value)}
                              className="w-4 h-4 rounded focus:ring-blue-500"
                              style={{ accentColor: BRAND }} />
                            <span className="text-gray-800 text-sm font-medium">{col.label}</span>
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {step === 2 && <UploadZone />}
              {step === 3 && <DonePanel />}
            </div>

            <div className="flex justify-between items-center gap-3 px-6 py-4 border-t bg-gray-50 flex-shrink-0">
              {step === 1 && (
                <>
                  <button onClick={() => setMode(null)}
                    className="px-5 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-sm">
                    Back
                  </button>
                  <button onClick={handleGenerateTemplate} disabled={loading || selectedFields.length === 0}
                    className="px-5 py-2 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm transition-colors"
                    style={{ backgroundColor: BRAND }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = BRAND_DARK}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = BRAND}>
                    <Download size={16} />
                    {loading ? "Generating…" : "Generate & Download Template"}
                  </button>
                </>
              )}
              {step === 2 && (
                <>
                  <button onClick={() => { setStep(1); setFile(null); }}
                    className="px-5 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-sm">
                    Back
                  </button>
                  <button onClick={handleImport} disabled={loading || !file}
                    className="px-5 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm">
                    <Upload size={16} />
                    {loading ? "Importing…" : "Import Contacts"}
                  </button>
                </>
              )}
              {step === 3 && (
                <button onClick={() => { onImport?.(); onClose(); }}
                  className="ml-auto px-5 py-2 text-white rounded-lg font-semibold text-sm"
                  style={{ backgroundColor: BRAND }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = BRAND_DARK}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = BRAND}>
                  Done
                </button>
              )}
            </div>
          </>
        )}

        {/* ── Direct Upload mode ── */}
        {mode === "direct" && (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <UploadZone />
            </div>
            <div className="flex justify-between items-center gap-3 px-6 py-4 border-t bg-gray-50 flex-shrink-0">
              <button onClick={() => { setMode(null); setFile(null); }}
                className="px-5 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-sm">
                Back
              </button>
              <button onClick={handleImport} disabled={loading || !file}
                className="px-5 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm">
                <Upload size={16} />
                {loading ? "Importing…" : "Import Contacts"}
              </button>
            </div>
          </>
        )}

        {/* ── Done (direct mode) ── */}
        {mode === "done" && (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <DonePanel />
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end flex-shrink-0">
              <button onClick={() => { onImport?.(); onClose(); }}
                className="px-5 py-2 text-white rounded-lg font-semibold text-sm"
                style={{ backgroundColor: BRAND }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = BRAND_DARK}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = BRAND}>
                Done
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default ImportContactsModal;